import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import {
  ArrowLeft,
  Camera,
  CheckCircle,
  AlertCircle,
  Clock,
  Calendar,
  Play,
  Square,
  Loader2,
  MapPin,
  RefreshCw,
  Shield,
  Timer,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
  created_at: string
}

interface LocationData {
  latitude: number
  longitude: number
  accuracy: number
  address?: string
}

type BriefingState = 'idle' | 'capturing_start' | 'verifying_start' | 'running' | 'capturing_end' | 'verifying_end' | 'submitting' | 'success' | 'error'

export default function C3SubmissionPage() {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [activePeriod, setActivePeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  // Briefing state
  const [briefingState, setBriefingState] = useState<BriefingState>('idle')
  const [startPhoto, setStartPhoto] = useState<File | null>(null)
  const [startPhotoPreview, setStartPhotoPreview] = useState<string | null>(null)
  const [, setEndPhoto] = useState<File | null>(null)
  const [endPhotoPreview, setEndPhotoPreview] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [, setEndTime] = useState<Date | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [notes, setNotes] = useState('')

  // Location
  const [startLocation, setStartLocation] = useState<LocationData | null>(null)
  const [, setEndLocation] = useState<LocationData | null>(null)

  // UI State
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null)

  // Current capture mode
  const [captureMode, setCaptureMode] = useState<'start' | 'end'>('start')

  // Track if initial restore has been done (to prevent persist effect from clearing on mount)
  const hasRestoredRef = useRef(false)

  // Restore briefing state on mount FIRST (handles app being killed while timer running)
  useEffect(() => {
    const savedState = localStorage.getItem('c3_briefing_state')
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState)
        if (parsed.state === 'running' && parsed.startTime) {
          const savedStartTime = new Date(parsed.startTime)
          // Validate it's a reasonable time (not more than 24 hours ago)
          const hoursSinceStart = (Date.now() - savedStartTime.getTime()) / (1000 * 60 * 60)
          if (hoursSinceStart < 24) {
            setStartTime(savedStartTime)
            setBriefingState('running')
            if (parsed.startPhotoPreview) setStartPhotoPreview(parsed.startPhotoPreview)
            if (parsed.startLocation) setStartLocation(parsed.startLocation)
            if (parsed.notes) setNotes(parsed.notes)
            // Calculate current elapsed time
            const diff = Math.floor((Date.now() - savedStartTime.getTime()) / 1000)
            setElapsedSeconds(diff)
          } else {
            // Too old, clear it
            localStorage.removeItem('c3_briefing_state')
          }
        }
      } catch {
        localStorage.removeItem('c3_briefing_state')
      }
    }
    hasRestoredRef.current = true
  }, [])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activePeriod && indicator) {
      fetchSubmissions()
    }
  }, [activePeriod, indicator])

  // Timer effect - uses real timestamps to survive screen lock/background
  useEffect(() => {
    if (briefingState === 'running' && startTime) {
      // Calculate elapsed immediately
      const updateElapsed = () => {
        const now = new Date()
        const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000)
        setElapsedSeconds(diff)
      }

      updateElapsed() // Update immediately
      timerRef.current = setInterval(updateElapsed, 1000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [briefingState, startTime])

  // Handle visibility change - recalculate timer when app returns from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && briefingState === 'running' && startTime) {
        // Recalculate elapsed time when app becomes visible again
        const now = new Date()
        const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000)
        setElapsedSeconds(diff)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [briefingState, startTime])

  // Persist briefing state to localStorage for recovery after screen lock
  useEffect(() => {
    // Don't clear localStorage during initial mount (before restore completes)
    if (!hasRestoredRef.current) return

    if (briefingState === 'running' && startTime) {
      localStorage.setItem('c3_briefing_state', JSON.stringify({
        state: briefingState,
        startTime: startTime.toISOString(),
        startPhotoPreview,
        startLocation,
        notes,
      }))
    } else if (briefingState === 'idle' || briefingState === 'success') {
      localStorage.removeItem('c3_briefing_state')
    }
  }, [briefingState, startTime, startPhotoPreview, startLocation, notes])

  const fetchData = async () => {
    try {
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const c3Indicator = indicators.find(i => i.code === 'C.3')
      if (c3Indicator) {
        setIndicator(c3Indicator)
      }

      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const currentDate = new Date()
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()

      let matchingPeriod = periodsData.find(
        p => p.month === currentMonth && p.year === currentYear && !p.is_locked
      )
      if (!matchingPeriod) {
        matchingPeriod = periodsData.find(p => !p.is_locked) || periodsData[0]
      }
      setActivePeriod(matchingPeriod || null)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!activePeriod?.id || !indicator?.id) return

    try {
      const scoresData = await apiGet<{
        scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
      }>('/kpi/scores', { period_id: activePeriod.id })

      const score = scoresData.scores.find(s => s.indicator.code === 'C.3')
      if (score) {
        setSubmissionCount(score.submission_count)
        setCalculatedPoints(score.calculated_score)
      }

      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: activePeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const fetchLocation = async (): Promise<LocationData | null> => {
    if (!navigator.geolocation) {
      return null
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData: LocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }

          if (isOnline) {
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
              )
              const data = await response.json()
              if (data.display_name) {
                locationData.address = data.display_name
              }
            } catch (err) {
              console.error('Failed to get address:', err)
            }
          }

          resolve(locationData)
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    })
  }

  const verifyImage = async (file: File): Promise<{ isValid: boolean; message: string }> => {
    if (!isOnline) {
      // Offline mode - block verification (must be online)
      return { isValid: false, message: 'You must be online to verify briefing photos' }
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('expected_type', 'briefing')

    try {
      const result = await apiUpload<{
        is_valid: boolean
        category: string
        confidence: number
        threshold: number
        combined_valid_score: number
        category_scores: Record<string, number>
        message?: string
      }>('/ai/classify-image', formData)

      // Debug: Log full AI response (deactivated)
      // console.log('[AI Debug] Full classification result:', JSON.stringify(result, null, 2))

      if (result.is_valid) {
        return {
          isValid: true,
          message: `Verified as ${result.category} (${Math.round(result.confidence * 100)}% confidence)`
        }
      } else {
        // Build detailed debug message for invalid results (kept for debugging)
        // const scores = result.category_scores || {}
        // const scoreDetails = Object.entries(scores)
        //   .map(([cat, score]) => `${cat}: ${Math.round((score as number) * 100)}%`)
        //   .join(', ')
        // const debugMsg = [
        //   `[Not Valid]`,
        //   `Category: ${result.category}`,
        //   `Confidence: ${Math.round((result.confidence || 0) * 100)}%`,
        //   `Threshold: ${Math.round((result.threshold || 0.35) * 100)}%`,
        //   `Combined Score: ${Math.round((result.combined_valid_score || 0) * 100)}%`,
        //   `Scores: ${scoreDetails}`
        // ].join('\n')
        // console.log('[AI Debug] Validation failed:', debugMsg)

        return {
          isValid: false,
          message: 'Photo tidak valid untuk briefing. Silakan ambil foto yang menunjukkan kegiatan briefing dengan jelas.'
        }
      }
    } catch (err) {
      console.error('Verification error:', err)
      // On error, block proceeding - verification is required
      return { isValid: false, message: 'Verification service error. Please try again.' }
    }
  }

  const handleStartBriefing = () => {
    setCaptureMode('start')
    setBriefingState('capturing_start')
    setError(null)
    // Trigger camera
    setTimeout(() => {
      cameraInputRef.current?.click()
    }, 100)
  }

  const handleStopBriefing = () => {
    setCaptureMode('end')
    setBriefingState('capturing_end')
    setError(null)
    // Trigger camera
    setTimeout(() => {
      cameraInputRef.current?.click()
    }, 100)
  }

  const handleCameraCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      // User cancelled - go back to previous state
      if (captureMode === 'start') {
        setBriefingState('idle')
      } else {
        setBriefingState('running')
      }
      return
    }

    // Check file size
    if (file.size > 16 * 1024 * 1024) {
      setError('Photo size must be less than 16MB')
      if (captureMode === 'start') {
        setBriefingState('idle')
      } else {
        setBriefingState('running')
      }
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      if (captureMode === 'start') {
        setStartPhotoPreview(reader.result as string)
      } else {
        setEndPhotoPreview(reader.result as string)
      }
    }
    reader.readAsDataURL(file)

    // Verify image
    if (captureMode === 'start') {
      setStartPhoto(file)
      setBriefingState('verifying_start')
      setVerificationMessage('Verifying briefing photo...')

      const verification = await verifyImage(file)
      setVerificationMessage(verification.message)

      if (verification.isValid) {
        // Get location
        const location = await fetchLocation()
        setStartLocation(location)

        // Start the timer
        const now = new Date()
        setStartTime(now)
        setElapsedSeconds(0)
        setBriefingState('running')
      } else {
        setError(verification.message)
        setStartPhoto(null)
        setStartPhotoPreview(null)
        setBriefingState('idle')
      }
    } else {
      setEndPhoto(file)
      setBriefingState('verifying_end')
      setVerificationMessage('Verifying end photo...')

      // console.log('[AI Debug] Verifying END photo...')
      const verification = await verifyImage(file)
      // console.log('[AI Debug] END photo verification result:', verification)
      setVerificationMessage(verification.message)

      if (verification.isValid) {
        // Get location
        const location = await fetchLocation()
        setEndLocation(location)

        // Stop timer and set end time
        if (timerRef.current) {
          clearInterval(timerRef.current)
        }
        const now = new Date()
        setEndTime(now)

        // Proceed to submission
        await handleSubmit(file, now, location)
      } else {
        // console.log('[AI Debug] END photo FAILED - Setting error:', verification.message)
        setError(verification.message)
        setEndPhoto(null)
        setEndPhotoPreview(null)
        setBriefingState('running')
      }
    }

    // Reset input
    if (cameraInputRef.current) {
      cameraInputRef.current.value = ''
    }
  }

  const handleSubmit = async (endPhotoFile: File, endTimeValue: Date, endLocationValue: LocationData | null) => {
    // Debug: Log which fields are present/missing (deactivated)
    // console.log('[Debug] handleSubmit check:', {
    //   activePeriod: !!activePeriod,
    //   indicator: !!indicator,
    //   startPhoto: !!startPhoto,
    //   startPhotoPreview: !!startPhotoPreview,
    //   startTime: !!startTime,
    //   endPhotoFile: !!endPhotoFile
    // })

    // Try to recreate startPhoto from preview if lost (happens after app restore)
    let startPhotoToUpload: File | Blob | null = startPhoto
    if (!startPhoto && startPhotoPreview) {
      // console.log('[Debug] Recreating startPhoto from preview...')
      try {
        const response = await fetch(startPhotoPreview)
        const blob = await response.blob()
        startPhotoToUpload = new File([blob], 'briefing_start.jpg', { type: 'image/jpeg' })
        // console.log('[Debug] Successfully recreated startPhoto from preview')
      } catch (err) {
        console.error('[Debug] Failed to recreate startPhoto:', err)
      }
    }

    if (!activePeriod || !indicator || !startPhotoToUpload || !startTime) {
      // Debug: track missing fields (deactivated)
      // const missing = []
      // if (!activePeriod) missing.push('activePeriod')
      // if (!indicator) missing.push('indicator')
      // if (!startPhotoToUpload) missing.push('startPhoto (lost and could not recover)')
      // if (!startTime) missing.push('startTime')
      // console.log('[Debug] Missing fields:', missing)

      setError('Data briefing tidak lengkap. Silakan mulai ulang briefing.')
      setBriefingState('running')
      return
    }

    setBriefingState('submitting')
    setError(null)

    const duration = Math.floor((endTimeValue.getTime() - startTime.getTime()) / 1000)
    const durationMinutes = Math.floor(duration / 60)
    const durationSeconds = duration % 60

    // Build notes with all data
    const startLocationStr = startLocation
      ? `${startLocation.latitude.toFixed(6)}, ${startLocation.longitude.toFixed(6)}${startLocation.address ? ` (${startLocation.address})` : ''}`
      : 'Location unavailable'

    const endLocationStr = endLocationValue
      ? `${endLocationValue.latitude.toFixed(6)}, ${endLocationValue.longitude.toFixed(6)}${endLocationValue.address ? ` (${endLocationValue.address})` : ''}`
      : 'Location unavailable'

    const fullNotes = [
      notes ? `Notes: ${notes}` : '',
      `Duration: ${durationMinutes}m ${durationSeconds}s`,
      `Start Time: ${startTime.toLocaleTimeString()}`,
      `End Time: ${endTimeValue.toLocaleTimeString()}`,
      `Start Location: ${startLocationStr}`,
      `End Location: ${endLocationStr}`,
    ].filter(Boolean).join('\n')

    try {
      const response = await apiPost<{ id: number }>('/kpi/submissions', {
        period_id: activePeriod.id,
        indicator_id: indicator.id,
        submission_date: new Date().toISOString().split('T')[0],
        value: 1,
        notes: fullNotes,
        metadata: {
          briefing_type: 'daily',
          start_time: startTime.toISOString(),
          end_time: endTimeValue.toISOString(),
          duration_seconds: duration,
          start_location: startLocation,
          end_location: endLocationValue,
        },
      })

      // Upload start photo (use recovered photo if original was lost)
      if (response.id && startPhotoToUpload) {
        const startFormData = new FormData()
        startFormData.append('file', startPhotoToUpload)
        startFormData.append('submission_id', String(response.id))
        startFormData.append('file_type', 'briefing_start')
        await apiUpload('/kpi/attachments', startFormData)
      }

      // Upload end photo
      if (response.id && endPhotoFile) {
        const endFormData = new FormData()
        endFormData.append('file', endPhotoFile)
        endFormData.append('submission_id', String(response.id))
        endFormData.append('file_type', 'briefing_end')
        await apiUpload('/kpi/attachments', endFormData)
      }

      setBriefingState('success')

      // Reset after delay
      setTimeout(() => {
        resetBriefing()
        fetchSubmissions()
      }, 3000)
    } catch (err) {
      console.error('Submission failed:', err)
      setError('Failed to submit. Please try again.')
      setBriefingState('running')
    }
  }

  const resetBriefing = () => {
    setBriefingState('idle')
    setStartPhoto(null)
    setStartPhotoPreview(null)
    setEndPhoto(null)
    setEndPhotoPreview(null)
    setStartTime(null)
    setEndTime(null)
    setElapsedSeconds(0)
    setStartLocation(null)
    setEndLocation(null)
    setNotes('')
    setError(null)
    setVerificationMessage(null)
    // Clear persisted state
    localStorage.removeItem('c3_briefing_state')
  }

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const progress = indicator ? Math.min((submissionCount / indicator.target) * 100, 100) : 0
  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="mobile-page bg-gray-50">
      {/* Hidden camera input - capture only, no gallery */}
      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
      />

      {/* Header */}
      <div className="bg-purple-500 px-4 pt-4 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">Daily Briefing</h1>
            <p className="text-white/80 text-xs">C.3 - Record Your Briefing</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 rounded-xl p-3">
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {submissionCount} / {indicator?.target || 40}
                </span>
                <span className="text-sm font-semibold text-purple-500">
                  {calculatedPoints.toFixed(2)} pts
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max {indicator?.max_points || 15} points
              </p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 mt-4 pb-32">
        {/* Date Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 rounded-lg p-2">
              <Calendar className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Today's Date</p>
              <p className="font-medium text-gray-900">{formattedDate}</p>
            </div>
          </div>
        </div>

        {/* Briefing States */}

        {/* IDLE STATE - Start Button */}
        {briefingState === 'idle' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="h-10 w-10 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Daily Briefing</h3>
              <p className="text-sm text-gray-500 mb-6">
                Take a photo to verify and start timing your briefing session
              </p>
              <button
                onClick={handleStartBriefing}
                disabled={!isOnline}
                className="w-full py-4 bg-purple-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-colors disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                <Camera className="h-5 w-5" />
                Start Daily Briefing
              </button>
              {!isOnline && (
                <p className="text-xs text-red-500 mt-2">You must be online to start a briefing</p>
              )}
            </div>
          </div>
        )}

        {/* CAPTURING START STATE */}
        {briefingState === 'capturing_start' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-purple-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Opening Camera...</h3>
              <p className="text-sm text-gray-500">Take a photo of the briefing session</p>
            </div>
          </div>
        )}

        {/* VERIFYING START STATE */}
        {briefingState === 'verifying_start' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              {startPhotoPreview && (
                <img
                  src={startPhotoPreview}
                  alt="Start"
                  className="w-32 h-32 object-cover rounded-xl mx-auto mb-4"
                />
              )}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="h-6 w-6 text-purple-500 animate-pulse" />
                <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verifying Photo</h3>
              <p className="text-sm text-gray-500">{verificationMessage || 'Analyzing image...'}</p>
            </div>
          </div>
        )}

        {/* RUNNING STATE - Timer */}
        {briefingState === 'running' && (
          <>
            <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-6 shadow-lg mb-4 text-white">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-purple-200">Briefing in Progress</span>
                </div>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Timer className="h-8 w-8" />
                  <span className="text-5xl font-bold font-mono">{formatTime(elapsedSeconds)}</span>
                </div>
                {startPhotoPreview && (
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <img
                      src={startPhotoPreview}
                      alt="Start"
                      className="w-16 h-16 object-cover rounded-lg border-2 border-white/30"
                    />
                    <div className="text-left text-sm">
                      <p className="text-purple-200">Started at</p>
                      <p className="font-medium">{startTime?.toLocaleTimeString()}</p>
                    </div>
                  </div>
                )}
                {startLocation && (
                  <div className="flex items-center justify-center gap-1 text-xs text-purple-200">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate max-w-xs">{startLocation.address || `${startLocation.latitude.toFixed(4)}, ${startLocation.longitude.toFixed(4)}`}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes while running */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Briefing Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add topics discussed, attendees, or other notes..."
                rows={3}
                className="mobile-textarea"
              />
            </div>

            {/* Stop Button */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
              <button
                onClick={handleStopBriefing}
                className="w-full py-4 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
              >
                <Square className="h-5 w-5" />
                Stop Briefing
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Take a photo to verify and end the briefing session
              </p>
            </div>
          </>
        )}

        {/* CAPTURING END STATE */}
        {briefingState === 'capturing_end' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-red-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Opening Camera...</h3>
              <p className="text-sm text-gray-500">Take a photo to end the briefing</p>
            </div>
          </div>
        )}

        {/* VERIFYING END STATE */}
        {briefingState === 'verifying_end' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              {endPhotoPreview && (
                <img
                  src={endPhotoPreview}
                  alt="End"
                  className="w-32 h-32 object-cover rounded-xl mx-auto mb-4"
                />
              )}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="h-6 w-6 text-purple-500 animate-pulse" />
                <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verifying End Photo</h3>
              <p className="text-sm text-gray-500">{verificationMessage || 'Analyzing image...'}</p>
            </div>
          </div>
        )}

        {/* SUBMITTING STATE */}
        {briefingState === 'submitting' && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-purple-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Submitting Briefing</h3>
              <p className="text-sm text-gray-500">Please wait...</p>
            </div>
          </div>
        )}

        {/* SUCCESS STATE */}
        {briefingState === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">Briefing Recorded!</h3>
              <p className="text-sm text-green-600 mb-4">
                Duration: {formatTime(elapsedSeconds)}
              </p>
              <div className="flex items-center justify-center gap-4">
                {startPhotoPreview && (
                  <div>
                    <img src={startPhotoPreview} alt="Start" className="w-16 h-16 object-cover rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">Start</p>
                  </div>
                )}
                {endPhotoPreview && (
                  <div>
                    <img src={endPhotoPreview} alt="End" className="w-16 h-16 object-cover rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">End</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
              {(briefingState === 'idle' || briefingState === 'running') && (
                <button
                  onClick={() => setError(null)}
                  className="text-sm text-red-600 font-medium mt-2 flex items-center gap-1"
                >
                  <RefreshCw className="h-4 w-4" />
                  Coba Lagi
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && briefingState === 'idle' && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Briefings
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className="bg-purple-100 rounded-lg p-2">
                    <Calendar className="h-5 w-5 text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-gray-500 truncate">{sub.notes.split('\n')[0]}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
